import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtErr } from "@/shared/lib/api";
import { Search, Receipt, FileText, IndianRupee, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * AccountsBilling — project-wise billing dashboard for the Accounts team.
 * Lists every active project with DC counts, invoice counts, and balance.
 * Click into any project to view its DC folder + upload Tally invoice.
 */
export default function AccountsBilling() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/accounts/projects-overview")
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(fmtErr(e.response?.data?.detail) || "Load failed"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = rows.filter((r) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (
      r.project_code.toLowerCase().includes(s) ||
      (r.name || "").toLowerCase().includes(s) ||
      (r.client_name || "").toLowerCase().includes(s)
    );
  });

  const totals = rows.reduce(
    (acc, r) => ({
      dcs: acc.dcs + (r.total_dcs || 0),
      pending: acc.pending + (r.pending_billing_dcs || 0),
      invoiced: acc.invoiced + (r.invoice_total || 0),
      balance: acc.balance + (r.balance_total || 0),
    }),
    { dcs: 0, pending: 0, invoiced: 0, balance: 0 }
  );

  return (
    <div className="p-6 lg:p-10" data-testid="accounts-billing-page">
      {/* Hero */}
      <div className="mb-8 rounded-3xl bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-700 text-white p-6 sm:p-10 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-72 h-72 bg-orange-500/10 rounded-full -mr-24 -mt-24 blur-3xl" />
        <div className="relative">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300">
            Accounts · Billing
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Project-wise Billing
          </h1>
          <p className="text-sm text-zinc-300 mt-2 max-w-2xl leading-relaxed">
            Click any project to see all its delivery challans grouped by line item.
            Tick the DCs you&apos;ve billed in Tally and upload the invoice PDF here.
            Done in &lt; 30 seconds.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
            <KpiHero label="Active Projects" value={rows.length} />
            <KpiHero label="Total DCs" value={totals.dcs} />
            <KpiHero label="Pending Billing" value={totals.pending} tone="amber" />
            <KpiHero label="Outstanding" value={`₹${(totals.balance || 0).toLocaleString("en-IN")}`} tone="rose" />
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 max-w-md">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search project, code, or client…"
            data-testid="accounts-search"
            className="w-full h-11 pl-9 pr-3 border border-zinc-300 rounded-full text-sm focus:outline-none focus:border-orange-500"
          />
        </div>
      </div>

      {/* Project list */}
      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Project</th>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-right px-4 py-3">DCs</th>
              <th className="text-right px-4 py-3">Pending</th>
              <th className="text-right px-4 py-3">Invoiced</th>
              <th className="text-right px-4 py-3">Balance</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-zinc-400">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-zinc-400">No projects.</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.project_code}
                  className="border-t border-zinc-100 hover:bg-orange-50/30 transition-colors"
                  data-testid={`acct-proj-row-${r.project_code}`}>
                <td className="px-4 py-3">
                  <div className="font-mono text-xs text-orange-600"><ProjRef code={r.project_code} /></div>
                  <div className="text-sm font-medium text-zinc-800 mt-0.5 truncate max-w-[260px]">{r.name}</div>
                </td>
                <td className="px-4 py-3 text-zinc-700">{r.client_name || "—"}</td>
                <td className="px-4 py-3 text-right font-mono">{r.total_dcs}</td>
                <td className="px-4 py-3 text-right">
                  {r.pending_billing_dcs > 0 ? (
                    <span className="inline-flex items-center px-2 h-6 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-mono font-semibold">
                      {r.pending_billing_dcs}
                    </span>
                  ) : <span className="text-zinc-300 font-mono">0</span>}
                </td>
                <td className="px-4 py-3 text-right font-mono text-zinc-700">
                  {r.invoice_total > 0 ? `₹${r.invoice_total.toLocaleString("en-IN")}` : "—"}
                </td>
                <td className="px-4 py-3 text-right font-mono">
                  {r.balance_total > 0 ? (
                    <span className="text-rose-600 font-semibold">
                      ₹{r.balance_total.toLocaleString("en-IN")}
                    </span>
                  ) : <span className="text-emerald-600">cleared</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/accounts/projects/${r.project_code}`}
                        className="inline-flex items-center px-3 h-8 rounded-full bg-zinc-900 text-white text-xs font-medium hover:bg-zinc-800"
                        data-testid={`acct-open-${r.project_code}`}>
                    Open
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone }) {
  const tones = {
    blue:  "text-blue-700  bg-blue-50  border-blue-100",
    amber: "text-amber-700 bg-amber-50 border-amber-100",
    rose:  "text-rose-700  bg-rose-50  border-rose-100",
  };
  const cls = tones[tone] || "text-zinc-700 bg-zinc-50 border-zinc-100";
  return (
    <div className={`rounded-2xl p-4 border ${cls}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">{label}</div>
        {Icon && <Icon className="w-4 h-4 opacity-60" />}
      </div>
      <div className="font-display text-2xl font-bold mt-1.5">{value}</div>
    </div>
  );
}

function KpiHero({ label, value, tone }) {
  const tones = {
    amber: "text-amber-300", rose: "text-rose-300",
  };
  return (
    <div className="bg-white/5 rounded-2xl p-3.5 border border-white/10">
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 ${tones[tone] || "text-white"}`}>{value}</div>
    </div>
  );
}
